import React, { useState } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, BookmarkIcon, ThumbsUpIcon, UserIcon, XIcon, CheckIcon, EditIcon, TrashIcon, BarChart2Icon, EyeIcon, MessageSquareIcon, TagIcon, UploadIcon, BookIcon, VideoIcon, FileTextIcon, ClipboardIcon, CodeIcon, GithubIcon, GlobeIcon } from 'lucide-react';
import { toast } from 'sonner';
interface Resource {
  id: number;
  title: string;
  type: 'article' | 'video' | 'template' | 'guide';
  categories: string[];
  creator: {
    name: string;
    image: string;
    role: string;
  };
  status: 'draft' | 'pending_review' | 'published' | 'rejected';
  reviewStatus?: {
    reviewers: {
      id: string;
      name: string;
      image: string;
      status: 'pending' | 'approved' | 'rejected';
      feedback?: string;
      reviewedAt?: string;
    }[];
    requiredReviews: number;
    submittedAt: string;
  };
  source?: {
    type: 'created' | 'imported';
    platform?: string;
    originalUrl?: string;
  };
}
interface ResourceHubProps {
  userType: 'mentor' | 'mentee' | null;
}
const MentorResourceHub: React.FC<ResourceHubProps> = ({
  userType
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState<Resource | null>(null);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState<Resource | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importPlatform, setImportPlatform] = useState<'medium' | 'dev.to' | 'github' | 'other'>('medium');
  const [viewMode, setViewMode] = useState<'personal' | 'discover'>('personal');
  const [showShareModal, setShowShareModal] = useState<{
    resource: Resource;
  } | null>(null);
  const resources: Resource[] = [{
    id: 1,
    title: 'Effective Mentorship Guide',
    type: 'guide',
    categories: ['Mentorship', 'Leadership'],
    creator: {
      name: 'Alex Morgan',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      role: 'Senior Mentor'
    },
    status: 'published',
    summary: 'A comprehensive guide to building effective mentorship relationships and fostering growth.',
    content: 'Full content here...',
    engagement: {
      views: 245,
      saves: 56,
      shares: 23,
      feedback: 12
    },
    lastUpdated: '2 days ago',
    image: 'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1674&q=80',
    reviews: [{
      reviewer: 'Jamie Chen',
      comment: 'Excellent resource for new mentors!',
      date: '1 day ago'
    }]
  }, {
    id: 2,
    title: 'Goal Setting Template',
    type: 'template',
    categories: ['Goals', 'Planning'],
    creator: {
      name: 'Alex Morgan',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      role: 'Senior Mentor'
    },
    status: 'published',
    summary: 'A structured template for setting and tracking mentee goals using the SMART framework.',
    content: 'Template content here...',
    engagement: {
      views: 189,
      saves: 45,
      shares: 18,
      feedback: 8
    },
    lastUpdated: '1 week ago',
    reviews: [{
      reviewer: 'Sam Taylor',
      comment: 'Using this with all my mentees now!',
      date: '5 days ago'
    }]
  }];
  const CreateResourceButtons = () => <div className="flex items-center space-x-3" data-id="element-534">
      <button onClick={() => setShowImportModal(true)} className="px-4 py-2 border border-purple-600 text-purple-600 rounded-lg flex items-center hover:bg-purple-50" data-id="element-535">
        <UploadIcon size={18} className="mr-2" data-id="element-536" />
        Import Resource
      </button>
      <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center hover:bg-purple-700" data-id="element-537">
        <PlusIcon size={18} className="mr-2" data-id="element-538" />
        Create Resource
      </button>
    </div>;
  const ImportResourceModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-539">
      <div className="bg-white rounded-xl w-full max-w-lg" data-id="element-540">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-541">
          <h3 className="font-semibold" data-id="element-542">Import External Resource</h3>
          <button onClick={() => setShowImportModal(false)} data-id="element-543">
            <XIcon size={20} data-id="element-544" />
          </button>
        </div>
        <div className="p-6" data-id="element-545">
          <div className="mb-6" data-id="element-546">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-547">
              Source Platform
            </label>
            <div className="grid grid-cols-4 gap-3" data-id="element-548">
              {[{
              id: 'medium',
              label: 'Medium',
              icon: <BookIcon size={20} data-id="element-549" />
            }, {
              id: 'dev.to',
              label: 'Dev.to',
              icon: <CodeIcon size={20} data-id="element-550" />
            }, {
              id: 'github',
              label: 'GitHub',
              icon: <GithubIcon size={20} data-id="element-551" />
            }, {
              id: 'other',
              label: 'Other',
              icon: <GlobeIcon size={20} data-id="element-552" />
            }].map(platform => <button key={platform.id} onClick={() => setImportPlatform(platform.id as any)} className={`p-3 border rounded-lg flex flex-col items-center gap-1 ${importPlatform === platform.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-600 hover:bg-purple-50'}`} data-id="element-553">
                  {platform.icon}
                  <span className="text-sm" data-id="element-554">{platform.label}</span>
                </button>)}
            </div>
          </div>
          <div className="mb-6" data-id="element-555">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-556">
              Resource URL
            </label>
            <input type="url" value={importUrl} onChange={e => setImportUrl(e.target.value)} placeholder="https://" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-557" />
          </div>
          <div className="bg-purple-50 rounded-lg p-4 mb-6" data-id="element-558">
            <h4 className="font-medium text-purple-800 mb-2" data-id="element-559">Import Process</h4>
            <ol className="space-y-2" data-id="element-560">
              <li className="flex items-start text-sm text-purple-700" data-id="element-561">
                <span className="w-5 h-5 bg-purple-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0" data-id="element-562">
                  1
                </span>
                We'll fetch the content and metadata from the provided URL
              </li>
              <li className="flex items-start text-sm text-purple-700" data-id="element-563">
                <span className="w-5 h-5 bg-purple-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0" data-id="element-564">
                  2
                </span>
                You'll be able to review and edit before submitting
              </li>
              <li className="flex items-start text-sm text-purple-700" data-id="element-565">
                <span className="w-5 h-5 bg-purple-200 rounded-full flex items-center justify-center mr-2 flex-shrink-0" data-id="element-566">
                  3
                </span>
                The resource will go through peer review before publishing
              </li>
            </ol>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-567">
            <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" data-id="element-568">
              Cancel
            </button>
            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-569">
              Import Resource
            </button>
          </div>
        </div>
      </div>
    </div>;
  const ResourceCard = ({
    resource
  }: {
    resource: Resource;
  }) => <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-570">
      <div className="p-4" data-id="element-571">
        <div className="flex justify-between items-start mb-4" data-id="element-572">
          <div data-id="element-573">
            <div className="flex items-center mb-2" data-id="element-574">
              {resource.type === 'guide' && <BookIcon size={16} className="text-purple-600 mr-2" data-id="element-575" />}
              {resource.type === 'video' && <VideoIcon size={16} className="text-purple-600 mr-2" data-id="element-576" />}
              {resource.type === 'template' && <ClipboardIcon size={16} className="text-purple-600 mr-2" data-id="element-577" />}
              {resource.type === 'article' && <FileTextIcon size={16} className="text-purple-600 mr-2" data-id="element-578" />}
              <h3 className="font-medium" data-id="element-579">{resource.title}</h3>
            </div>
            <div className="flex flex-wrap gap-1 mb-2" data-id="element-580">
              {resource.categories.map(category => <span key={category} className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-581">
                  {category}
                </span>)}
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-582">
            <button onClick={() => setShowAnalyticsModal(resource)} className="p-1 text-gray-400 hover:text-purple-600" data-id="element-583">
              <BarChart2Icon size={16} data-id="element-584" />
            </button>
            <button className="p-1 text-gray-400 hover:text-purple-600" data-id="element-585">
              <EditIcon size={16} data-id="element-586" />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2" data-id="element-587">
          {resource.summary}
        </p>
        <div className="flex items-center justify-between" data-id="element-588">
          <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="element-589">
            <span className="flex items-center" data-id="element-590">
              <EyeIcon size={14} className="mr-1" data-id="element-591" />
              {resource.engagement.views}
            </span>
            <span className="flex items-center" data-id="element-592">
              <BookmarkIcon size={14} className="mr-1" data-id="element-593" />
              {resource.engagement.saves}
            </span>
            <span className="flex items-center" data-id="element-594">
              <MessageSquareIcon size={14} className="mr-1" data-id="element-595" />
              {resource.engagement.feedback}
            </span>
          </div>
          <button onClick={() => setShowResourceModal(resource)} className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-596">
            View Details
          </button>
        </div>
        {resource.status !== 'published' && <div className="mb-4 flex items-center" data-id="element-597">
            <div className={`
              px-2 py-1 rounded-full text-xs font-medium
              ${resource.status === 'draft' ? 'bg-gray-100 text-gray-600' : ''}
              ${resource.status === 'pending_review' ? 'bg-yellow-100 text-yellow-700' : ''}
              ${resource.status === 'rejected' ? 'bg-red-100 text-red-600' : ''}
            `} data-id="element-598">
              {resource.status === 'draft' && 'Draft'}
              {resource.status === 'pending_review' && 'Under Review'}
              {resource.status === 'rejected' && 'Needs Revision'}
            </div>
            {resource.reviewStatus && <div className="ml-2 flex items-center" data-id="element-599">
                <div className="flex -space-x-2" data-id="element-600">
                  {resource.reviewStatus.reviewers.map((reviewer, idx) => <div key={reviewer.id} className={`w-6 h-6 rounded-full border-2 border-white ${reviewer.status === 'approved' ? 'bg-green-100' : reviewer.status === 'rejected' ? 'bg-red-100' : 'bg-gray-100'}`} data-id="element-601">
                      <img src={reviewer.image} alt={reviewer.name} className="w-full h-full rounded-full" data-id="element-602" />
                    </div>)}
                </div>
                <span className="text-xs text-gray-500 ml-2" data-id="element-603">
                  {resource.reviewStatus.reviewers.filter(r => r.status !== 'pending').length}
                  /{resource.reviewStatus.requiredReviews} reviews
                </span>
              </div>}
          </div>}
      </div>
    </div>;
  const CreateResourceModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-604">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-605">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-606">
          <h3 className="font-semibold" data-id="element-607">Create New Resource</h3>
          <button onClick={() => setShowCreateModal(false)} data-id="element-608">
            <XIcon size={20} data-id="element-609" />
          </button>
        </div>
        <div className="p-6" data-id="element-610">
          <div className="space-y-6" data-id="element-611">
            <div data-id="element-612">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-613">
                Resource Type
              </label>
              <div className="grid grid-cols-4 gap-3" data-id="element-614">
                {['guide', 'video', 'template', 'article'].map(type => <button key={type} className="p-3 border border-gray-200 rounded-lg flex flex-col items-center hover:border-purple-600 hover:bg-purple-50" data-id="element-615">
                    {type === 'guide' && <BookIcon size={20} data-id="element-616" />}
                    {type === 'video' && <VideoIcon size={20} data-id="element-617" />}
                    {type === 'template' && <ClipboardIcon size={20} data-id="element-618" />}
                    {type === 'article' && <FileTextIcon size={20} data-id="element-619" />}
                    <span className="text-sm mt-1 capitalize" data-id="element-620">{type}</span>
                  </button>)}
              </div>
            </div>
            <div data-id="element-621">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-622">
                Title
              </label>
              <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter resource title" data-id="element-623" />
            </div>
            <div data-id="element-624">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-625">
                Categories
              </label>
              <div className="grid grid-cols-3 gap-2" data-id="element-626">
                {['Mentorship', 'Leadership', 'Career Growth', 'Goals', 'Planning', 'Communication', 'Technical', 'Soft Skills', 'Diversity'].map(category => <label key={category} className="flex items-center" data-id="element-627">
                    <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-628" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-629">
                      {category}
                    </span>
                  </label>)}
              </div>
            </div>
            <div data-id="element-630">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-631">
                Summary
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" rows={3} placeholder="Brief description of the resource" data-id="element-632" />
            </div>
            <div data-id="element-633">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-634">
                Content
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" rows={8} placeholder="Main content of your resource" data-id="element-635" />
            </div>
            <div data-id="element-636">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-637">
                Cover Image (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-638">
                <div className="flex flex-col items-center" data-id="element-639">
                  <UploadIcon size={24} className="text-gray-400 mb-2" data-id="element-640" />
                  <p className="text-sm text-gray-600" data-id="element-641">
                    Drag and drop an image, or click to browse
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3" data-id="element-642">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" data-id="element-643">
              Cancel
            </button>
            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-644">
              Submit for Review
            </button>
          </div>
        </div>
      </div>
    </div>;
  const ResourceDetailsModal = ({
    resource
  }: {
    resource: Resource;
  }) => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-645">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-646">
        <div className="p-6" data-id="element-647">
          <div className="flex justify-between items-start mb-6" data-id="element-648">
            <div data-id="element-649">
              <h2 className="text-xl font-semibold mb-2" data-id="element-650">{resource.title}</h2>
              <div className="flex items-center" data-id="element-651">
                <img src={resource.creator.image} alt={resource.creator.name} className="w-6 h-6 rounded-full mr-2" data-id="element-652" />
                <span className="text-sm text-gray-600" data-id="element-653">
                  {resource.creator.name}
                </span>
              </div>
            </div>
            <button onClick={() => setShowResourceModal(null)} data-id="element-654">
              <XIcon size={20} className="text-gray-500" data-id="element-655" />
            </button>
          </div>
          {resource.image && <img src={resource.image} alt={resource.title} className="w-full h-48 object-cover rounded-lg mb-6" data-id="element-656" />}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-657">
            <span className="text-sm bg-purple-100 text-purple-600 px-3 py-1 rounded-full" data-id="element-658">
              {resource.type}
            </span>
            {resource.categories.map(category => <span key={category} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full" data-id="element-659">
                {category}
              </span>)}
          </div>
          <div className="prose max-w-none mb-6" data-id="element-660">
            <p className="text-gray-600" data-id="element-661">{resource.content}</p>
          </div>
          <div className="border-t border-gray-200 pt-6 mt-6" data-id="element-662">
            <h3 className="font-medium mb-4" data-id="element-663">Engagement</h3>
            <div className="grid grid-cols-4 gap-4" data-id="element-664">
              <div className="bg-gray-50 p-3 rounded-lg text-center" data-id="element-665">
                <div className="text-xl font-semibold text-purple-600" data-id="element-666">
                  {resource.engagement.views}
                </div>
                <div className="text-sm text-gray-600" data-id="element-667">Views</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center" data-id="element-668">
                <div className="text-xl font-semibold text-purple-600" data-id="element-669">
                  {resource.engagement.saves}
                </div>
                <div className="text-sm text-gray-600" data-id="element-670">Saves</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center" data-id="element-671">
                <div className="text-xl font-semibold text-purple-600" data-id="element-672">
                  {resource.engagement.shares}
                </div>
                <div className="text-sm text-gray-600" data-id="element-673">Shares</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center" data-id="element-674">
                <div className="text-xl font-semibold text-purple-600" data-id="element-675">
                  {resource.engagement.feedback}
                </div>
                <div className="text-sm text-gray-600" data-id="element-676">Comments</div>
              </div>
            </div>
          </div>
          {resource.reviews.length > 0 && <div className="border-t border-gray-200 pt-6 mt-6" data-id="element-677">
              <h3 className="font-medium mb-4" data-id="element-678">Recent Feedback</h3>
              <div className="space-y-4" data-id="element-679">
                {resource.reviews.map((review, index) => <div key={index} className="bg-gray-50 p-4 rounded-lg" data-id="element-680">
                    <p className="text-gray-600 mb-2" data-id="element-681">{review.comment}</p>
                    <div className="text-sm text-gray-500" data-id="element-682">
                      {review.reviewer} · {review.date}
                    </div>
                  </div>)}
              </div>
            </div>}
          <div className="flex justify-end mt-6" data-id="element-683">
            <button className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-684">
              Edit Resource
            </button>
          </div>
        </div>
      </div>
    </div>;
  const FilterModal = () => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-685">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-686">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-687">
          <h3 className="font-semibold" data-id="element-688">Filter Resources</h3>
          <button onClick={() => setShowFilters(false)} data-id="element-689">
            <XIcon size={20} data-id="element-690" />
          </button>
        </div>
        <div className="p-4" data-id="element-691">
          <div className="mb-6" data-id="element-692">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-693">
              Resource Type
            </h4>
            <div className="space-y-2" data-id="element-694">
              {['Guide', 'Video', 'Template', 'Article'].map(type => <label key={type} className="flex items-center" data-id="element-695">
                  <input type="checkbox" checked={selectedTypes.includes(type)} onChange={() => {
                setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-696" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-697">{type}</span>
                </label>)}
            </div>
          </div>
          <div className="mb-6" data-id="element-698">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-699">
              Categories
            </h4>
            <div className="grid grid-cols-2 gap-2" data-id="element-700">
              {['Mentorship', 'Leadership', 'Career Growth', 'Goals', 'Planning', 'Communication'].map(category => <label key={category} className="flex items-center" data-id="element-701">
                  <input type="checkbox" checked={selectedCategories.includes(category)} onChange={() => {
                setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-702" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-703">{category}</span>
                </label>)}
            </div>
          </div>
          <div className="mb-6" data-id="element-704">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-705">Status</h4>
            <div className="space-y-2" data-id="element-706">
              {['Published', 'Draft', 'Under Review'].map(status => <label key={status} className="flex items-center" data-id="element-707">
                  <input type="checkbox" checked={selectedStatus.includes(status)} onChange={() => {
                setSelectedStatus(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
              }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-708" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-709">{status}</span>
                </label>)}
            </div>
          </div>
          <div className="flex justify-end" data-id="element-710">
            <button onClick={() => {
            setSelectedTypes([]);
            setSelectedCategories([]);
            setSelectedStatus([]);
          }} className="text-sm px-3 py-1 text-gray-700 hover:bg-gray-50 rounded-full" data-id="element-711">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-712">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>;
  const ShareResourceModal = () => {
    if (!showShareModal) return null;
    const [selectedMentee, setSelectedMentee] = useState('');
    const [message, setMessage] = useState('');
    const mentees = [{
      id: '1',
      name: 'Jordan Lee'
    }, {
      id: '2',
      name: 'Sam Chen'
    }];
    const handleShare = () => {
      toast.success(`Resource shared with ${mentees.find(m => m.id === selectedMentee)?.name}`);
      setShowShareModal(null);
    };
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-713">
        <div className="bg-white rounded-xl w-full max-w-md" data-id="element-714">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-715">
            <h3 className="font-semibold" data-id="element-716">Share Resource</h3>
            <button onClick={() => setShowShareModal(null)} data-id="element-717">
              <XIcon size={20} data-id="element-718" />
            </button>
          </div>
          <div className="p-4" data-id="element-719">
            <div className="mb-4 bg-gray-50 rounded-lg p-3" data-id="element-720">
              <h4 className="font-medium text-sm mb-1" data-id="element-721">
                {showShareModal.resource.title}
              </h4>
              <div className="flex flex-wrap gap-1 mb-2" data-id="element-722">
                {showShareModal.resource.categories.map(category => <span key={category} className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-723">
                    {category}
                  </span>)}
              </div>
              <p className="text-sm text-gray-600" data-id="element-724">
                {showShareModal.resource.summary}
              </p>
            </div>
            <div className="mb-4" data-id="element-725">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-726">
                Select Mentee
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={selectedMentee} onChange={e => setSelectedMentee(e.target.value)} data-id="element-727">
                <option value="" data-id="element-728">Choose a mentee...</option>
                {mentees.map(mentee => <option key={mentee.id} value={mentee.id} data-id="element-729">
                    {mentee.name}
                  </option>)}
              </select>
            </div>
            <div className="mb-4" data-id="element-730">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-731">
                Add a Note (Optional)
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" rows={3} value={message} onChange={e => setMessage(e.target.value)} placeholder="Why are you sharing this resource?" data-id="element-732" />
            </div>
            <div className="flex justify-end space-x-3" data-id="element-733">
              <button onClick={() => setShowShareModal(null)} className="text-sm px-3 py-1 text-gray-700 hover:bg-gray-50 rounded-full" data-id="element-734">
                Cancel
              </button>
              <button onClick={handleShare} disabled={!selectedMentee} className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-735">
                Share Resource
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const reviewQueue = [{
    id: 1,
    title: 'Inclusive Design Practices',
    author: 'Jamie Chen',
    type: 'Article',
    status: 'pending_review',
    submittedAt: '2 days ago',
    reviewsReceived: 1,
    requiredReviews: 2,
    feedback: [{
      reviewer: 'Alex Morgan',
      comment: 'Great content, but needs more examples',
      status: 'needs_revision',
      date: '1 day ago'
    }]
  }, {
    id: 2,
    title: 'Leadership in Tech',
    author: 'Sam Taylor',
    type: 'Guide',
    status: 'ready_to_post',
    submittedAt: '3 days ago',
    reviewsReceived: 2,
    requiredReviews: 2,
    feedback: []
  }, {
    id: 3,
    title: 'Building Effective Teams',
    author: 'Pat Rivera',
    type: 'Article',
    status: 'needs_review',
    submittedAt: '1 day ago',
    reviewsReceived: 0,
    requiredReviews: 2,
    feedback: []
  }];
  const peerReviewRequests = [{
    id: 1,
    title: 'Building Inclusive Teams',
    author: 'Pat Rivera',
    type: 'Guide',
    submittedAt: '1 day ago',
    requiredReviews: 2,
    currentReviews: 1
  }, {
    id: 2,
    title: 'Remote Work Best Practices',
    author: 'Jordan Lee',
    type: 'Article',
    submittedAt: '2 days ago',
    requiredReviews: 2,
    currentReviews: 0
  }];
  return <div className="pb-4" data-id="element-736">
      <div className="mb-6" data-id="element-737">
        <h1 className="text-xl font-bold mb-4" data-id="element-738">Resource Hub</h1>
        <div className="bg-gray-100 p-1 rounded-full inline-flex mb-4" data-id="element-739">
          <button className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${viewMode === 'personal' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setViewMode('personal')} data-id="element-740">
            My Resources
          </button>
          <button className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${viewMode === 'discover' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setViewMode('discover')} data-id="element-741">
            Discover & Share
          </button>
        </div>
        {viewMode === 'personal' && <div className="flex items-center space-x-2 mb-4" data-id="element-742">
            <button onClick={() => setShowCreateModal(true)} className="animate-slide-fade-first px-4 py-1.5 text-sm bg-purple-600 text-white rounded-full hover:bg-purple-700 flex items-center" data-id="element-743">
              <PlusIcon size={14} className="mr-1.5" data-id="element-744" />
              Create
            </button>
            <button onClick={() => setShowImportModal(true)} className="animate-slide-fade-second px-4 py-1.5 text-sm bg-purple-50 text-purple-600 rounded-full hover:bg-purple-100 flex items-center" data-id="element-745">
              <UploadIcon size={14} className="mr-1.5" data-id="element-746" />
              Import
            </button>
          </div>}
        <div className="flex items-center space-x-4" data-id="element-747">
          <div className="flex-1 relative" data-id="element-748">
            <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-749" />
            <input type="text" placeholder="Search resources..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-750" />
          </div>
          <button onClick={() => setShowFilters(true)} className="px-4 py-2 border border-gray-300 rounded-lg flex items-center hover:bg-gray-50" data-id="element-751">
            <FilterIcon size={18} className="mr-2" data-id="element-752" />
            Filters
          </button>
        </div>
      </div>
      {viewMode === 'personal' ? <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-753">
            {resources.map(resource => <ResourceCard key={resource.id} resource={resource} data-id="element-754" />)}
          </div>
          <div className="mb-8" data-id="element-755">
            <h2 className="text-lg font-semibold mb-4" data-id="element-756">Review Queue</h2>
            <div className="space-y-4" data-id="element-757">
              {reviewQueue.map(item => <div key={item.id} className="bg-white rounded-xl shadow-sm p-4" data-id="element-758">
                  <div className="flex justify-between items-start" data-id="element-759">
                    <div className="flex items-center" data-id="element-760">
                      {item.type === 'Guide' && <BookIcon size={16} className="text-purple-600 mr-1.5" data-id="element-761" />}
                      {item.type === 'Article' && <FileTextIcon size={16} className="text-purple-600 mr-1.5" data-id="element-762" />}
                      {item.type === 'Template' && <ClipboardIcon size={16} className="text-purple-600 mr-1.5" data-id="element-763" />}
                      {item.type === 'Video' && <VideoIcon size={16} className="text-purple-600 mr-1.5" data-id="element-764" />}
                      <h3 className="font-semibold text-base" data-id="element-765">{item.title}</h3>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full
                        ${item.status === 'needs_review' ? 'bg-[#FDEAD7] text-[#D97706]' : ''}
                        ${item.status === 'pending_review' ? 'bg-[#FEF3C7] text-[#92400E]' : ''}
                        ${item.status === 'ready_to_post' ? 'bg-[#D1FAE5] text-[#065F46]' : ''}
                      `} data-id="element-766">
                      {item.status === 'needs_review' && 'Needs Review'}
                      {item.status === 'pending_review' && 'Pending Review'}
                      {item.status === 'ready_to_post' && 'Ready to Post'}
                    </span>
                  </div>
                  <div className="mt-3" data-id="element-767">
                    <p className="text-sm text-gray-500" data-id="element-768">
                      By {item.author} · {item.submittedAt}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-769">
                      {item.reviewsReceived} of {item.requiredReviews} reviews
                      received
                    </p>
                    {item.feedback.length > 0 && <div className="bg-gray-50 rounded-lg p-3 mt-3" data-id="element-770">
                        <h4 className="text-sm font-medium mb-2" data-id="element-771">Feedback</h4>
                        {item.feedback.map((f, idx) => <div key={idx} className="text-sm text-gray-600" data-id="element-772">
                            <p className="mb-1" data-id="element-773">{f.comment}</p>
                            <p className="text-xs text-gray-500" data-id="element-774">
                              {f.reviewer} · {f.date}
                            </p>
                          </div>)}
                      </div>}
                  </div>
                  <div className="flex justify-end mt-3" data-id="element-775">
                    {item.status === 'needs_review' && <button className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" title="Share with mentors to begin peer review" data-id="element-776">
                        Send for Feedback
                      </button>}
                    {item.status === 'pending_review' && <button className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" title="Edit based on feedback before resubmitting" data-id="element-777">
                        Edit Resource
                      </button>}
                    {item.status === 'ready_to_post' && <button className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" title="Review how your resource will appear when published" data-id="element-778">
                        Preview Resource
                      </button>}
                  </div>
                </div>)}
            </div>
          </div>
          <div data-id="element-779">
            <h2 className="text-lg font-semibold mb-4" data-id="element-780">Peer Review Requests</h2>
            <div className="space-y-4" data-id="element-781">
              {peerReviewRequests.map(request => <div key={request.id} className="bg-white rounded-xl shadow-sm p-4" data-id="element-782">
                  <div className="flex justify-between items-start" data-id="element-783">
                    <div className="flex items-center" data-id="element-784">
                      {request.type === 'Guide' && <BookIcon size={16} className="text-purple-600 mr-1.5" data-id="element-785" />}
                      {request.type === 'Article' && <FileTextIcon size={16} className="text-purple-600 mr-1.5" data-id="element-786" />}
                      <h3 className="font-semibold text-base" data-id="element-787">
                        {request.title}
                      </h3>
                    </div>
                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded-full" data-id="element-788">
                      {request.type}
                    </span>
                  </div>
                  <div className="mt-3" data-id="element-789">
                    <p className="text-sm text-gray-500" data-id="element-790">
                      By {request.author} · {request.submittedAt}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-791">
                      {request.currentReviews}/{request.requiredReviews} reviews
                    </p>
                  </div>
                  <div className="flex justify-end mt-3" data-id="element-792">
                    <button className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-793">
                      Start Review
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-794">
          {resources.map(resource => <div key={resource.id} className="bg-white rounded-xl shadow-sm p-4" data-id="element-795">
              <div className="flex justify-between items-start mb-3" data-id="element-796">
                <div className="flex items-center" data-id="element-797">
                  {resource.type === 'guide' && <BookIcon size={16} className="text-purple-600 mr-1.5" data-id="element-798" />}
                  {resource.type === 'article' && <FileTextIcon size={16} className="text-purple-600 mr-1.5" data-id="element-799" />}
                  {resource.type === 'template' && <ClipboardIcon size={16} className="text-purple-600 mr-1.5" data-id="element-800" />}
                  {resource.type === 'video' && <VideoIcon size={16} className="text-purple-600 mr-1.5" data-id="element-801" />}
                  <h3 className="font-semibold text-base" data-id="element-802">{resource.title}</h3>
                </div>
                <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded-full" data-id="element-803">
                  {resource.type}
                </span>
              </div>
              <div className="mt-3" data-id="element-804">
                <div className="flex flex-wrap gap-1 mb-2" data-id="element-805">
                  {resource.categories.map(category => <span key={category} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full" data-id="element-806">
                      {category}
                    </span>)}
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-4" data-id="element-807">
                  {resource.summary}
                </p>
                <div className="flex items-center space-x-4 mb-3 text-sm text-gray-500" data-id="element-808">
                  <span className="flex items-center" data-id="element-809">
                    <EyeIcon size={14} className="mr-1" data-id="element-810" />
                    {resource.engagement?.views || 0}
                  </span>
                  <span className="flex items-center" data-id="element-811">
                    <BookmarkIcon size={14} className="mr-1" data-id="element-812" />
                    {resource.engagement?.saves || 0}
                  </span>
                  <span className="flex items-center" data-id="element-813">
                    <MessageSquareIcon size={14} className="mr-1" data-id="element-814" />
                    {resource.engagement?.feedback || 0}
                  </span>
                </div>
                <div className="flex justify-end space-x-4" data-id="element-815">
                  <button onClick={() => setShowResourceModal(resource)} className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-816">
                    View Details
                  </button>
                  <button onClick={() => setShowShareModal({
              resource
            })} className="text-sm bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700" data-id="element-817">
                    Share with Mentee
                  </button>
                </div>
              </div>
            </div>)}
        </div>}
      {showCreateModal && <CreateResourceModal data-id="element-818" />}
      {showResourceModal && <ResourceDetailsModal resource={showResourceModal} data-id="element-819" />}
      {showFilters && <FilterModal data-id="element-820" />}
      {showShareModal && <ShareResourceModal resource={resources.find(r => r.id === showShareModal.resourceId)} onClose={() => setShowShareModal(null)} data-id="element-821" />}
      {userType === 'mentor' && <div className="mb-6" data-id="element-822">
          <h2 className="text-lg font-semibold mb-4" data-id="element-823">Review Queue</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-824">
            {resources.filter(r => r.status === 'pending_review').map(resource => <ResourceCard key={resource.id} resource={resource} data-id="element-825" />)}
          </div>
        </div>}
      {userType === 'mentor' && showImportModal && <ImportResourceModal data-id="element-826" />}
    </div>;
};
export default MentorResourceHub;